import React, { useState, useEffect } from "react";
import "../../scss/index.scss";
import useWindowSize from "../../utils/useWindowSize";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import MiddleEllipsis from "react-middle-ellipsis";
import { CopyToClipboard } from "react-copy-to-clipboard";

const SubAffiliateBadge = ({ label }) => (
  <span className="shVia" title={`via ${label}`}>
    <span className="vd" />
    via {label}
  </span>
);

// Product label (KOL_CAMPAIGNS_AGENCY_PLAN.md §5) derived from the legacy
// columns the `transactions` action now returns. An older ppapi omits
// `walletType` entirely → no badge (a split leg must never be labelled "Swap").
export function productOf(item) {
  if (!item || item.walletType == null) return null;
  const wt = String(item.walletType || "").toUpperCase();
  if (wt === "PERPETUALS") return "perps";
  if (wt === "SPOT") return "spot";
  if (String(item.product || "").toLowerCase() === "payment") return "payment";
  if (item.externalCustomerId) return "split";
  return "swap";
}

const PRODUCT_STYLE = {
  swap: { label: "Swap", color: "#5aa2ff" },
  split: { label: "Split", color: "#c79bff" },
  payment: { label: "Payment", color: "#e6c06a" },
  spot: { label: "Spot", color: "#3ad17e" },
  perps: { label: "Perps", color: "#ff8a5b" },
};

export const ProductBadge = ({ product, privacy }) => {
  const s = PRODUCT_STYLE[product];
  if (!s) return null;
  return (
    <span className="shVia" style={{ color: s.color, background: `${s.color}1f`, borderColor: `${s.color}55` }} title={privacy ? `${s.label} · ${privacy}` : s.label}>
      {s.label}{privacy ? ` · ${privacy}` : ""}
    </span>
  );
};

const CampaignBadge = ({ code }) => (
  <span className="shVia" style={{ color: "#aeb6b0", background: "rgba(255,255,255,.05)", borderColor: "rgba(255,255,255,.14)", fontFamily: "monospace" }} title={`campaign ${code}`}>
    {code}
  </span>
);

const StatusPill = ({ orderState }) => {
  if (orderState === "Deposit Not Completed") {
    return (
      <span className="pill fail">
        <span className="pd" />
        Failed
      </span>
    );
  }
  if (orderState === "Completed") {
    return (
      <span className="pill done">
        <span className="pd" />
        {orderState}
      </span>
    );
  }
  if (orderState === "Refunded") {
    return (
      <span className="pill pend">
        <span className="pd" />
        {orderState}
      </span>
    );
  }
  return (
    <span className="pill pend">
      <span className="pd" />
      {orderState}
    </span>
  );
};

const TableDataTransactions = ({ item, currentDiv, tableOpen, openModal }) => {
  const sized = useWindowSize();

  const [openDiv, setOpenDiv] = useState(false);
  const [copiedDeposit, setCopiedDeposit] = useState(false);

  const openTable = () => {
    if (tableOpen === "") {
      setOpenDiv(true);
      currentDiv(item.id, openDiv);
    } else if (tableOpen === item.id) {
      setOpenDiv(!openDiv);
    } else if (tableOpen !== "" && tableOpen !== item.id) {
      setOpenDiv(true);
      currentDiv(item.id);
    }
  };

  // Reset copy status after a short delay
  useEffect(() => {
    if (copiedDeposit) {
      setTimeout(() => {
        setCopiedDeposit(false);
      }, 2500);
    }
  }, [copiedDeposit]);

  const cleanAmount = item.depositAmount
    ? item.depositAmount.replace(/^0+(?!\.)|(?:\.|(\..*?))0+$/gm, "$1")
    : item.depositAmount;
  const partnerProfit =
    item.partnerProfit != null ? item.partnerProfit : item.profit;
  const subProfit = item.subAffiliateProfit;
  const hasSubProfit = subProfit != null && parseFloat(subProfit) > 0;
  const noProfit =
    item.orderState === "Deposit Not Completed" ||
    item.orderState === "Refund" ||
    parseFloat(partnerProfit) === 0;

  // Prefer the walletless (Postgres) transaction id so the id shown here matches
  // what the walletless public API expects. Fall back to the legacy id only for
  // old legacy-only transactions where walletlessTxid is null.
  const displayId = item.walletlessTxid || item.id;
  const product = productOf(item);
  const isTrade = product === "spot" || product === "perps";
  // Perps/spot rows: chainId carries the side, depositEurVolume the USD notional.
  const side = isTrade ? String(item.chainId || "").toUpperCase() : null;
  const notional = isTrade && item.depositEurVolume ? Number(item.depositEurVolume) : null;

  const pairCell = (
    <div style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0, flexWrap: "wrap" }}>
      {!isTrade && item.givenCoinImage?.small && (
        <div className="coinPair">
          <span className="coinB">
            <img src={item.givenCoinImage.small} alt={item.givenCoin} />
          </span>
          <span className="coinB">
            <img src={item.getCoinImage?.small} alt={item.getCoin} />
          </span>
        </div>
      )}
      <span className="cellStrong">
        {isTrade
          ? <>{side ? `${side} ` : ""}{item.givenCoin}{item.getCoin && item.getCoin !== item.givenCoin ? `/${item.getCoin}` : ""}{notional ? <small style={{ color: "#7c847e" }}> · ${notional.toLocaleString(undefined, { maximumFractionDigits: 2 })}</small> : null}</>
          : <>{cleanAmount} {item.givenCoin} → {item.getCoin}</>}
      </span>
      {product && <ProductBadge product={product} privacy={item.splitPrivacy} />}
      {item.campaignCode && <CampaignBadge code={item.campaignCode} />}
      {item.subAffiliate && <SubAffiliateBadge label={item.subAffiliate} />}
    </div>
  );

  const profitCell = noProfit ? (
    <span className="cellStrong" style={{ color: "#7c847e" }}>
      No Profit
    </span>
  ) : (
    <span
      className="cellStrong"
      style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
    >
      <span className="cellFee">{parseFloat(partnerProfit).toFixed(8)}</span>
      {hasSubProfit && (
        <span style={{ color: "#7fe3a6", fontWeight: 600 }}>
          / {parseFloat(subProfit).toFixed(8)}
        </span>
      )}
      {item.profitImageCurrency && (
        <img
          src={item.profitImageCurrency}
          alt="profit-currency"
          width="18"
          height="18"
          style={{ borderRadius: "50%" }}
        />
      )}
    </span>
  );

  const txCell = item.txnHash ? (
    <CopyToClipboard text={item.txnHash} onCopy={() => setCopiedDeposit(true)}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
          minWidth: 0,
          maxWidth: 150,
        }}
      >
        {!copiedDeposit ? (
          <div style={{ minWidth: 0, whiteSpace: "nowrap", flex: 1 }}>
            <MiddleEllipsis>
              <span className="cellMono">{item.txnHash}</span>
            </MiddleEllipsis>
          </div>
        ) : (
          <span style={{ color: "#3ad17e", fontWeight: 700, fontSize: 12 }}>Copied!</span>
        )}
        <FontAwesomeIcon color="#3ad17e" icon={faCopy} />
      </div>
    </CopyToClipboard>
  ) : (
    <span className="cellMono">-</span>
  );

  if (sized.width > 1024) {
    return (
      <div
        className="gtRow"
        style={{
          gridTemplateColumns:
            "110px minmax(220px,1.5fr) minmax(180px,1.2fr) 120px minmax(150px,1fr) 170px",
        }}
      >
        <div className="cellMono">#{displayId}</div>
        {pairCell}
        <div>{profitCell}</div>
        <div>
          <StatusPill orderState={item.orderState} />
        </div>
        <div>{txCell}</div>
        <div className="cellDate" style={{ color: "#7c847e" }}>
          {item.dateTimeCreated}
        </div>
      </div>
    );
  }

  const expanded = openDiv && tableOpen === item.id;

  return (
    <div className="mCard">
      <div className="mTop" onClick={openTable}>
        {pairCell}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "none" }}>
          <StatusPill orderState={item.orderState} />
          <span style={{ color: "#5d645f", fontSize: 12 }}>{expanded ? "▴" : "▾"}</span>
        </div>
      </div>

      {expanded && (
        <div className="mBody">
          <div className="mKV">
            <span className="k">ID</span>
            <span className="v">#{displayId}</span>
          </div>
          <div className="mKV">
            <span className="k">Amount</span>
            <span className="v" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {isTrade ? (notional ? `$${notional.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—") : cleanAmount}
              {item.givenCoinImage?.small && (
                <img
                  src={item.givenCoinImage.small}
                  alt="giveCoin-img"
                  width="16"
                  height="16"
                  style={{ borderRadius: "50%" }}
                />
              )}
            </span>
          </div>
          <div className="mKV">
            <span className="k">Profit</span>
            <span className="v">{profitCell}</span>
          </div>
          <div className="mKV">
            <span className="k">Txn Hash</span>
            <span className="v" style={{ maxWidth: 180 }}>
              {txCell}
            </span>
          </div>
          <div className="mKV">
            <span className="k">Client Email</span>
            <span className="v">
              {item.clientEmail}
              {item.clientEmail === "none" && typeof openModal === "function" && (
                <button
                  type="button"
                  className="btnGhost sm"
                  style={{ marginLeft: 8 }}
                  onClick={() => openModal(true)}
                >
                  Edit
                </button>
              )}
            </span>
          </div>
          <div className="mKV">
            <span className="k">Date created</span>
            <span className="v">{item.dateTimeCreated}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableDataTransactions;
